import ApiSmile from '@/api/api-smile';
import ActionArea from '@/component/action-area';
import {ActionButton} from '@/component/action-area/action-area';
import DataRow from '@/component/data-row';
import PageContainer from '@/component/page-container';
import LabelUtil from '@/util/label-util';
import LoadingUtil from '@/util/loading-util';
import MessageUtil from '@/util/message-util';
import Utils from '@/util/utils';
import {DatePicker, Divider, Form, Input, InputNumber, Modal, Select, Tooltip} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import moment, {Moment} from 'moment';
import {InfoData} from '../types';
import './info-detail.scss';
import {InfoCircleOutlined, StarFilled, StarOutlined} from '@ant-design/icons';
import {TagColor} from '@/model/const';

interface InfoBrief {
    id: number;
    code: string;
    name: string;
}

interface UpdateData {
    code: string;
    name: string;
    industry: string;
    description: string;
    interestDate?: Moment;
    interestRate?: string;
    expireTime?: Moment;
    tagStar?: number;
    tagColor?: number;
}

const InterestRateTitle = ['第一年', '第二年', '第三年', '第四年', '第五年', '第六年', '到期补偿'];

const InfoDetail = () => {
    const {code} = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState<InfoData>();
    const [isEdit, setIsEdit] = useState(false);
    const [form] = Form.useForm();
    const isNew = !(Number(code) > 0);
    const [infoBrief, setInfoBrief] = useState<InfoBrief[]>([]);
    const [inputInfoBrief, setInputInfoBrief] = useState<InfoBrief>();
    const [tagStarEdit, setTagStarEdit] = useState(0);

    useEffect(() => {
        if (!Utils.isNaturalNumber(code)) {
            navigate('/', {replace: true});
            return;
        }

        const load = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.getInfoDetail(code || '');
            if (response?.data?.code === 0) {
                setData(response.data.data);
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        if (Number(code) > 0) {
            if (code?.length !== 6) {
                navigate('/', {replace: true});
            } else {
                load();
            }
        }
    }, [navigate, code]);

    useEffect(() => {
        const load = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.getInfoBrief();
            if (response?.data?.code === 0) {
                setInfoBrief(response.data.data || []);
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        if (isNew) {
            load();
        }
    }, [isNew]);

    useEffect(() => {
        if (isNew) {
            setIsEdit(true);
        }
    }, [isNew, form]);

    const onResetEdit = () => {
        setIsEdit(false);
        form.resetFields();
    };

    const onCancelClick = () => {
        if (isNew) {
            navigate('/info', {replace: true});
        } else {
            onResetEdit();
        }
    };

    const onCreateData = () => {
        const value = form.getFieldsValue() || {};
        const payload: UpdateData = {
            code: value.code || '',
            ...getUpdateData(value),
        };
        if (!validateUpdateData(payload)) {
            return;
        }
        const exist = infoBrief.find((item) => item.code === payload.code);
        if (exist) {
            MessageUtil.error('代码在系统中已存在');
            return;
        }

        const create = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.createInfoDetail(payload);
            if (response?.data?.code === 0) {
                navigate(`/info/${response.data.data.code}`, {replace: true});
                onResetEdit();
                MessageUtil.info('保存成功');
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };
        Modal.confirm({
            title: '确定保存吗？',
            content: getConfirmContent(payload),
            onOk: create,
        });
    };

    const getUpdateData = (value: any) => {
        const rate = InterestRateTitle.map((_item, index) => value[`year${index}`] || 0);
        return {
            name: value.name || '',
            industry: value.industry || '',
            description: value.description || '',
            interestDate: value.interestDate,
            interestRate: rate.join(','),
            expireTime: value.expireTime,
            tagStar: tagStarEdit || 0,
            tagColor: value.tagColor || 0,
        };
    };

    const validateUpdateData = (payload: UpdateData) => {
        if (!payload.code) {
            MessageUtil.error('代码不能为空');
            return false;
        }
        if (!Utils.isPositiveInteger(payload.code) || payload.code.length !== 6) {
            MessageUtil.error('代码格式错误');
            return false;
        }
        if (!payload.name) {
            MessageUtil.error('名称不能为空');
            return false;
        }
        return true;
    };

    const getConfirmContent = (payload: UpdateData) => {
        return (
            <div>
                <div>{`代码：${payload.code}`}</div>
                <div>{`名称：${payload.name}`}</div>
                <div>{`行业：${payload.industry}`}</div>
                <div>{`起息：${payload.interestDate?.format('YYYY-MM-DD') || '--'}`}</div>
                <div>{`利率：${payload.interestRate}`}</div>
                <div>{`到期：${payload.expireTime?.format('YYYY-MM-DD') || '--'}`}</div>
                <div>{`星标：${payload.tagStar}`}</div>
                <div style={{display: 'flex'}}>
                    {`颜色：`}
                    <div style={getTagColorStyle(TagColor[(payload.tagColor || 0) % TagColor.length])} />
                </div>
                <div>{`描述：${payload.description}`}</div>
            </div>
        );
    };

    const onUpdateData = () => {
        const value = form.getFieldsValue() || {};
        const payload: UpdateData = {
            code: data?.code || '',
            ...getUpdateData(value),
        };
        if (!validateUpdateData(payload)) {
            return;
        }

        const update = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.updateInfoDetail(payload);
            if (response?.data?.code === 0) {
                setData(response.data.data);
                onCancelClick();
                MessageUtil.info('保存成功');
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        Modal.confirm({
            title: '确定更新吗？',
            content: getConfirmContent(payload),
            onOk: update,
        });
    };

    const onSaveClick = () => {
        if (isNew) {
            onCreateData();
        } else {
            onUpdateData();
        }
    };

    const onEditClick = () => {
        setIsEdit(true);
        const rate = (data?.interestRate || '').split(',');
        const years: any = {};
        InterestRateTitle.forEach((_item, index) => {
            years[`year${index}`] = index < rate.length ? rate[index] || 0 : 0;
        });
        form.setFieldsValue({
            ...data,
            interestDate: data?.interestDate && moment(data?.interestDate || 0),
            ...years,
            expireTime: data?.expireTime && moment(data?.expireTime || 0),
        });
        setTagStarEdit(data?.tagStar || 0);
    };

    const getButtons = () => {
        const buttons: ActionButton[] = [];
        if (isEdit) {
            buttons.push({text: '取消', onClick: onCancelClick});
            buttons.push({text: '保存', onClick: onSaveClick, type: 'primary'});
        } else {
            if (data) {
                buttons.push({text: '编辑', onClick: onEditClick});
            }
        }
        return buttons;
    };

    const onCodeChange = (value: number) => {
        const exist = infoBrief.find((item) => item.code === `${value}`);
        setInputInfoBrief(exist);
    };

    const renderInterestRate = () => {
        const rate = (data?.interestRate || '').split(',');

        const getRateText = (index: number) => {
            if (index < rate.length && rate[index] && Number(rate[index])) {
                return Utils.formatPercentage(Number(rate[index]), 1);
            }
            return '--';
        };

        return (
            <div className="interest-rate-wrap">
                {InterestRateTitle.map((item, index) => {
                    const length = InterestRateTitle.length;
                    return (
                        <div key={item} className="item">
                            <div className="content">
                                <div className="data-desc">
                                    {item}
                                    {index === length - 1 && (
                                        <Tooltip title={'不含最后一期'}>
                                            <InfoCircleOutlined className="info" />
                                        </Tooltip>
                                    )}
                                </div>
                                {isEdit ? (
                                    <Form.Item name={`year${index}`}>
                                        <InputNumber className="data-desc input" stringMode step={'0.1'} min={0} />
                                    </Form.Item>
                                ) : (
                                    <div className="data-desc rate">{getRateText(index)}</div>
                                )}
                            </div>
                            {index < length - 1 && <Divider type="vertical" className="divider" />}
                        </div>
                    );
                })}
            </div>
        );
    };

    const getTagColorStyle = (color: string) => {
        return {
            backgroundColor: color,
            width: '48px',
            height: '18px',
            borderRadius: '2px',
            margin: '3px 0',
            border: 'border: solid 0.5px #5C5E62',
        };
    };

    return (
        <PageContainer className="page-info-detail">
            <ActionArea buttons={getButtons()} />
            <Form form={form}>
                <DataRow title="代码">
                    {isEdit && isNew ? (
                        <div className="exist-tip-wrap">
                            <Form.Item name={'code'}>
                                <InputNumber className="data-desc data-input" stringMode min={0} onChange={onCodeChange} />
                            </Form.Item>
                            {inputInfoBrief && <div className="exist-tip">{`${inputInfoBrief.name || ''}(${inputInfoBrief.code || ''})`}</div>}
                        </div>
                    ) : (
                        data?.code || ''
                    )}
                </DataRow>
                <DataRow title="名称">
                    {isEdit ? (
                        <Form.Item name={'name'}>
                            <Input className="data-desc data-input" />
                        </Form.Item>
                    ) : (
                        data?.name || ''
                    )}
                </DataRow>
                <DataRow title="行业">
                    {isEdit ? (
                        <Form.Item name={'industry'}>
                            <Input className="data-desc data-input" />
                        </Form.Item>
                    ) : (
                        data?.industry || ''
                    )}
                </DataRow>
                <DataRow title="起息日期">
                    {isEdit ? (
                        <Form.Item name={'interestDate'}>
                            <DatePicker />
                        </Form.Item>
                    ) : (
                        Utils.formatDate(data?.interestDate)
                    )}
                </DataRow>
                <DataRow title="利率说明">{renderInterestRate()}</DataRow>
                <DataRow title="到期时间">
                    {isEdit ? (
                        <Form.Item name={'expireTime'}>
                            <DatePicker />
                        </Form.Item>
                    ) : (
                        Utils.formatDate(data?.expireTime)
                    )}
                </DataRow>
                <DataRow title="加星标记">
                    {isEdit ? (
                        !!tagStarEdit ? (
                            <StarFilled className="star star-fill" onClick={() => setTagStarEdit(0)} />
                        ) : (
                            <StarOutlined className="star" onClick={() => setTagStarEdit(1)} />
                        )
                    ) : data?.tagStar ? (
                        <StarFilled className="star star-fill" />
                    ) : (
                        <StarOutlined className="star" />
                    )}
                </DataRow>
                <DataRow title="颜色标记">
                    {isEdit ? (
                        <Form.Item name={'tagColor'}>
                            <Select optionFilterProp="children">
                                {TagColor.map((item, index) => (
                                    <Select.Option key={item} value={index}>
                                        <div style={getTagColorStyle(item)} />
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    ) : (
                        <div className="color" style={{backgroundColor: TagColor[(data?.tagColor || 0) % TagColor.length]}} />
                    )}
                </DataRow>
                <DataRow title="描述">
                    {isEdit ? (
                        <Form.Item name={'description'}>
                            <TextArea rows={4} className="data-desc" />
                        </Form.Item>
                    ) : (
                        data?.description || ''
                    )}
                </DataRow>
            </Form>
        </PageContainer>
    );
};

export default InfoDetail;
